import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, User, Phone, Mail, Truck, MapPin, Clock } from 'lucide-react';

export default function DriverSidebar({
  filteredDrivers, driverLoadCounts, selectedDriverId, setSelectedDriverId,
  selectedDriver, driverSearch, setDriverSearch, driverFilter, setDriverFilter,
  hasLoadsOnly, setHasLoadsOnly,
}) {
  const formatCode = (id) => (id || '').slice(-6).toUpperCase();

  const formatLocation = (loc) => {
    if (!loc) return 'Unknown';
    if (loc.lat && loc.lng) return `${loc.lat.toFixed(4)}, ${loc.lng.toFixed(4)}`;
    return 'Unknown';
  };

  const formatLastSeen = (loc) => {
    if (!loc?.updated_at) return 'N/A';
    return new Date(loc.updated_at).toLocaleString();
  };

  return (
    <div className="flex flex-col h-full bg-card border-r border-border">
      {/* Filters */}
      <div className="p-3 space-y-2 border-b border-border">
        <Select value={driverFilter} onValueChange={setDriverFilter}>
          <SelectTrigger className="bg-muted border-input text-foreground h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-muted border-input">
            <SelectItem value="all">ALL</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="idle">Idle</SelectItem>
          </SelectContent>
        </Select>

        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Checkbox
            checked={hasLoadsOnly}
            onCheckedChange={setHasLoadsOnly}
            className="border-input data-[state=checked]:bg-brand-accent data-[state=checked]:border-brand-accent"
          />
          Has Loads?
        </label>

        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={driverSearch}
            onChange={e => setDriverSearch(e.target.value)}
            className="bg-muted border-input text-foreground h-7 text-xs pl-7"
          />
        </div>
      </div>

      {/* Header */}
      <div className="px-3 py-2 border-b border-border">
        <span className="text-[10px] font-semibold text-muted-foreground tracking-wider">
          DRIVER NAME ({filteredDrivers.length})
        </span>
      </div>

      {/* Driver List */}
      <ScrollArea className="flex-1">
        <div className="divide-y divide-border/50">
          {filteredDrivers.map(driver => {
            const counts = driverLoadCounts.get(driver.user_id) || { active: 0, pending: 0, completed: 0, total: 0 };
            const isSelected = selectedDriverId === driver.user_id;
            return (
              <div
                key={driver.user_id}
                className={`px-3 py-2 cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-brand-accent/10 border-l-2 border-l-brand-accent'
                    : 'hover:bg-muted/50 border-l-2 border-l-transparent'
                }`}
                onClick={() => setSelectedDriverId(isSelected ? null : driver.user_id)}
              >
                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => setSelectedDriverId(isSelected ? null : driver.user_id)}
                    className="mt-0.5 border-input data-[state=checked]:bg-brand-accent data-[state=checked]:border-brand-accent"
                    onClick={e => e.stopPropagation()}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-medium text-foreground truncate">{driver.name}</span>
                      {counts.active > 0 && (
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[9px] px-1 py-0">
                          HCT
                        </Badge>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      (Code: {formatCode(driver.user_id)})
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      ({counts.active}|{counts.pending}|{counts.completed}|{counts.total})
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {filteredDrivers.length === 0 && (
            <div className="text-center py-8 text-muted-foreground text-xs">No drivers found</div>
          )}
        </div>
      </ScrollArea>

      {/* Selected Driver Detail */}
      {selectedDriver && (
        <div className="border-t border-border p-3 bg-surface/80 space-y-1">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-brand-accent/20 flex items-center justify-center">
              <User className="w-3 h-3 text-brand-accent" />
            </div>
            <span className="text-xs font-semibold text-foreground">{selectedDriver.name}</span>
          </div>
          {selectedDriver.phone && (
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <Phone className="w-3 h-3" />
              <span>P: {selectedDriver.phone}</span>
            </div>
          )}
          {selectedDriver.email && (
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <Mail className="w-3 h-3" />
              <span>E: {selectedDriver.email}</span>
            </div>
          )}
          {selectedDriver.truck_type && (
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <Truck className="w-3 h-3" />
              <span>{selectedDriver.truck_type}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <MapPin className="w-3 h-3" />
            <span>Last: {formatLocation(selectedDriver.last_location)}</span>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <Clock className="w-3 h-3" />
            <span>{formatLastSeen(selectedDriver.last_location)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
